import { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { DataGrid, esES } from '@mui/x-data-grid';
import useTranslation from 'Hooks/useTranslation';
import useCourseAssignment from '../Hooks/useCourseAssignment';

const BORDER = '#E2E5EB';
const TEXT_MUTED = '#8A93A6';
const TEXT_STRONG = '#1E293B';

const IconSearch = ({ size = 15, color = TEXT_MUTED }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.5-4.5" />
  </svg>
);

const searchBoxSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: '9px',
  border: `1px solid ${BORDER}`,
  borderRadius: '8px',
  bgcolor: '#fff',
  mb: 2,
};

const searchInputStyle = {
  border: 'none',
  outline: 'none',
  fontSize: 13,
  flex: 1,
  color: TEXT_STRONG,
  background: 'transparent',
  width: '100%',
  fontFamily: 'inherit',
};

export default function CourseAssignmentDialog({ open, onClose, selectedJobTitleIds, onSuccess }) {
  const { t } = useTranslation();
  const {
    courses,
    loadingCourses,
    assigning,
    assignCourses,
    pagination,
    page,
    pageSize,
    search,
    setPage,
    setPageSize,
    setSearch,
  } = useCourseAssignment();
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const previousPageIds = useRef(new Set());

  const handleSelectionModelChange = (newSelection) => {
    const currentPageIds = new Set(courses.map(c => c.id));
    const newSelectionSet = new Set(newSelection);

    const preservedSelections = selectedCourseIds.filter(id => !currentPageIds.has(id));
    const currentPageSelections = newSelection.filter(id => currentPageIds.has(id));

    setSelectedCourseIds([...preservedSelections, ...currentPageSelections]);
    previousPageIds.current = currentPageIds;
  };

  const currentSelectionModel = selectedCourseIds.filter(id =>
    courses.some(c => c.id === id)
  );

  const handleConfirm = async () => {
    try {
      await assignCourses(selectedCourseIds, selectedJobTitleIds);
      setSelectedCourseIds([]);
      onSuccess();
      onClose();
    } catch {
      // assignment failed — dialog stays open, user can retry
    }
  };

  const handleClose = () => {
    setSelectedCourseIds([]);
    onClose();
  };

  const columns = [
    { field: 'name', headerName: t('JobTitle.CourseName'), flex: 2 },
    {
      field: 'duration',
      headerName: t('JobTitle.CourseDuration'),
      flex: 1,
      valueGetter: params => {
        const minutes = params.row.duration;
        if (!minutes) return '—';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      },
    },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>{t('JobTitle.assignCoursesTitle')}</span>
          {selectedCourseIds.length > 0 && (
            <Chip
              label={`${selectedCourseIds.length} seleccionado${selectedCourseIds.length !== 1 ? 's' : ''}`}
              color="primary"
              size="small"
              sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={searchBoxSx}>
          <IconSearch />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('JobTitle.CourseSearch')}
            style={searchInputStyle}
          />
        </Box>
        <Box sx={{ height: 400 }}>
          <DataGrid
            density="compact"
            rows={courses}
            columns={columns}
            loading={loadingCourses}
            checkboxSelection
            disableSelectionOnClick
            disableColumnMenu
            hideFooterSelectedRowCount
            selectionModel={currentSelectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            paginationMode="server"
            rowCount={pagination?.totalItems || 0}
            page={page}
            onPageChange={newPage => setPage(newPage)}
            pageSize={pageSize}
            onPageSizeChange={newSize => setPageSize(newSize)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={assigning}>
          {t('JobTitle.assignCoursesCancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={assigning || selectedCourseIds.length === 0}
        >
          {assigning ? '...' : t('JobTitle.assignCoursesConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
